import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { useTranslation } from "react-i18next";

import { useColorPicker } from "@hooks/settings";
import { useNotification } from "@hooks/useNotification";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { SvgCalendarBlack, SvgHourBlack } from "@routes/IconeSvg";
import {
  AGENDA_MODEL_CREATE_SUCCESS,
  AGENDA_MODEL_UPDATE_SUCCESS,
  agendaModelActions
} from "@store/actions/settings/_agendaModelAction";
// @ts-ignore
import "antd/dist/antd.css";
import "date-fns";
import { endOfDay } from "date-fns";
import moment from "moment-timezone";
import { SketchPicker } from "react-color";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";

// @ts-ignore
import "@assets/css/pages/recruitments/pipeline.css";
// @ts-ignore
import "@css/components/datePickerDropdown.css";
// @ts-ignore
import "@css/pages/hoursPlanner/hoursPlanner.css";

// import ModalIndex from "@Modals/layout/index";
import ModalVisibility from "@Modals/modalVisibilities";
import DateRangePickerDropdown from "@components/dateRange/DropdownDateRange";
import FormSimple from "@components/formGroup/formSimple";
import RecurrenceInput from "@components/input/RecurrenceInput";
import {
  ListboxSimple,
  Switch,
  TextArea
} from "@components/tailwindComponents";
// import ListboxEmploye from "@components/tailwindComponents/ListboxEmploye";
import FormGroupColorPicker from "@pages/entreprise/recruitments/components/formGroup/formGroupColorPicker";

import { presetColors } from "@helpers/_configs";
import {
  getDateToRender,
  getDatesAndHoursHeading,
  setNotificationError
} from "@helpers/_functions";
import { AgendaModel, HoraireService } from "@services/all.services";
import { NotificationUtil } from "@utils/notification.utils";

import CustomNotificationError from "../CustomNotificationError";
import InputTimepickerModal from "../InputTimepickerModal";
import LunchHoursPicker from "../LunchHoursPicker";
import TeleworkButton from "../TeleworkButton";
import { useFormValidation } from "../sharedUtils/formValidation";
import WeeklyHoursSection, {
  buildDefaultWeeklyHours,
  WEEK_DAYS
} from "./subComponents/WeeklyHoursSection";
import { useHourCreateSideEffects } from "./utils/sideEffects";
import { useClearErrorsOnChange } from "./utils/useClearErrorsOnChange";

function ModalHourCreate(props) {
  const { t: hourTranslator } = useTranslation("hours.vacances");
  const { dispatch, showError, showSuccess, showInfo } = useNotification();

  const userId = useReduxSelector(({ userConnected }) => userConnected._id);
  const userAdmin = useReduxSelector(
    ({ settings }) => settings.enterprise.accountId
  );

  const methods = useForm({ mode: "onSubmit", reValidateMode: "onSubmit" });

  const {
    setColor,
    color,
    setValidated,
    onChangeColorPicker,
    handleChangeComplete
  } = useColorPicker({
    colorProps: null,
    isRequired: false
  });
  const {
    control,
    setValue,
    watch,
    handleSubmit,
    getValues,
    clearErrors,
    getFieldState,
    reset
  } = methods;

  useClearErrorsOnChange({ watch, clearErrors, getFieldState });

  const startDateTime = useWatch({
    name: "startDateTime",
    control
  });
  const endDateTime = useWatch({
    name: "endDateTime",
    control
  });
  const type = useWatch({
    name: "type",
    control
  });

  const [headerDateText, setHeaderDateText] = useState("");
  const [headerTimeText, setHeaderTimeText] = useState("");
  const [isRecurrenceShowed, setRecurrenceShowed] = useState(false);
  const [expandedDayKeys, setExpandedDayKeys] = useState(["monday"]);

  const { isLoaded, list: agendaModels } = useReduxSelector(
    (state) => state.agendaModel
  );

  const sortedAgendaModels = useMemo(
    () =>
      (agendaModels?.slice() || [])
        .filter((model) => !model.isArchived)
        .sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, {
            numeric: true,
            sensitivity: "base"
          })
        ),
    [agendaModels]
  );

  const employees = useReduxSelector((state) => state.commonData.employers);

  const resetByAgendModel = (_agendaModel) => {
    const [
        startDateTime,
        endDateTime,
        lunchStartDate,
        lunchEndDate,
        recurrenceEndDate
      ] = [
        _agendaModel.startDateTime,
        _agendaModel.endDateTime,
        _agendaModel.lunchStartDate,
        _agendaModel.lunchEndDate,
        _agendaModel.recurrenceEndDate
      ].map((date) => getDateToRender(date, _agendaModel.timezone)),
      Employees =
        _agendaModel.Employees?.map(({ _id }) =>
          employees.find(({ id }) => id === _id)
        ).filter(Boolean) || [];
    reset({
      ..._agendaModel,
      Employees,
      agendaModel: _agendaModel,
      dateRange: {
        startDate: startDateTime,
        endDate: endDateTime
      },
      startDateTime,
      endDateTime,
      lunchStartDate,
      lunchEndDate,
      recurrenceEndDate,
      startTime: startDateTime,
      endTime: endDateTime,
      weeklyHours: buildDefaultWeeklyHours(_agendaModel)
    });
    setColor({
      valueHexadecimale: _agendaModel.color,
      rgba: ""
    });
    setLunchHourShowed(_agendaModel.isLunchHourShowed);
    setRecurrenceShowed(_agendaModel.isRecurrenceShowed);
    const { dates, hours } = getDatesAndHoursHeading(
      _agendaModel.startDateTime,
      _agendaModel.endDateTime
    );
    setHeaderDateText(dates);
    setHeaderTimeText(hours);
    setExpandedDayKeys(
      WEEK_DAYS.map(
        ({ key: dayName }) =>
          _agendaModel.weeklyHours?.[dayName]?.startDateTime &&
          _agendaModel.weeklyHours?.[dayName]?.endDateTime &&
          dayName
      ).filter(Boolean)
    );
  };

  useEffect(() => {
    if (props.agendaModel?._id) {
      resetByAgendModel(props.agendaModel);
    } else {
      reset({
        isTeleworkActive: false,
        workingPlace: "",
        employes: [],
        dateRange: {},
        note: "",
        startDateTime: null,
        startTime: null,
        endDateTime: null,
        endTime: null,
        type: props.agendaModel?.type || "daily",
        recurrenceRepetitionWeek: 1,
        weeklyHours: buildDefaultWeeklyHours()
      });
      setColor({
        valueHexadecimale: "",
        rgba: ""
      });
      setLunchHourShowed(false);
      setRecurrenceShowed(false);
      setHeaderDateText("");
      setHeaderTimeText("");
      if (!isLoaded) dispatch(agendaModelActions.loadData);
    }
    setValidated(false);
    setSaving(false);
  }, [props.isOpen]);

  useEffect(() => {
    const { dates, hours } = getDatesAndHoursHeading(
      startDateTime,
      endDateTime
    );
    setHeaderDateText(dates);
    setHeaderTimeText(hours);
  }, [startDateTime, endDateTime]);
  const [isLunchHourShowed, setLunchHourShowed] = useState(false);

  function toggleLunchHour() {
    setLunchHourShowed((_state) => !_state);
  }

  const formValidation = useFormValidation({ control, getValues });
  useHourCreateSideEffects({
    setValue,
    control,
    type,
    isRecurrenceShowed
  });

  const [isSaving, setSaving] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const colorPickerRef = useRef(null);
  const colorPickerTriggerRef = useRef(null);

  useEffect(
    () => setValue("color", color?.valueHexadecimale?.toString()),
    [color]
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target) &&
        !colorPickerTriggerRef.current.contains(event.target)
      ) {
        setIsColorPickerOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function onSaveClicked(formValue) {
    console.log("formValue", formValue);
    if (formValue.recurrenceEndDate) {
      formValue.recurrenceEndDate = endOfDay(formValue.recurrenceEndDate);
    }
    setValidated(true);
    setSaving(true);
    formValue = {
      ...formValue,
      userId,
      userAdmin,
      isLunchHourShowed,
      isRecurrenceShowed,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      title:
        moment(formValue.startDateTime).format("HH:mm") +
        " - " +
        moment(formValue.endDateTime).format("HH:mm"),
      Enterprise: localStorage.getItem("enterpriseId")
    };

    if (type === "weekly") {
      formValue.weeklyHours = WEEK_DAYS.reduce((acc, day) => {
        const {
          startDateTime,
          startTime,
          endDateTime,
          endTime,
          isLunchHourShowed,
          lunchStartDate,
          lunchEndDate
        } = formValue.weeklyHours[day.key];
        const title =
          moment(startDateTime).format("HH:mm") +
          " - " +
          moment(endDateTime).format("HH:mm");
        if (startTime && endTime)
          acc[day.key] = {
            title,
            startDateTime,
            endDateTime,
            isLunchHourShowed,
            lunchStartDate,
            lunchEndDate
          };
        return acc;
      }, {});
    } else delete formValue.weeklyHours;

    if (props.isHourModel) {
      if (formValue.Employees?.length)
        formValue.Employees = formValue.Employees.map(({ id }) => id);
      props.agendaModel?._id
        ? AgendaModel.updateById(formValue)
            .then((response) => {
              if (response?.data?.data?._id) {
                dispatch({
                  type: AGENDA_MODEL_UPDATE_SUCCESS,
                  payload: response.data.data
                });
                showSuccess("update_action_success", { object: "agendaModel" });
                props.onClickClose();
              }
            })
            .catch((err) => console.error(err))
            .finally(() => {
              setSaving(false);
              setValidated(false);
            })
        : AgendaModel.insert(formValue)
            .then((response) => {
              if (response?.data?.data?._id) {
                dispatch({
                  type: AGENDA_MODEL_CREATE_SUCCESS,
                  payload: response.data.data
                });
                showSuccess("creation_action_success", {
                  object: "agendaModel"
                });
                props.onClickClose();
              }
            })
            .catch((err) => console.error(err))
            .finally(() => {
              setSaving(false);
              setValidated(false);
            });
    } else {
      HoraireService.insert({
        formValue,
        isRecurrenceShowed
      })
        .then(async (response) => {
          const insertedHours = response?.data?.data;
          if (insertedHours?.length) {
            const resNotif =
              await NotificationUtil.scheduleAssignment(insertedHours);
            if (resNotif?.data?.deniedNotification) showInfo("ATTENTION");
            props.refreshHours(new Date(insertedHours[0].start));
          }
          showSuccess("creation_action_success", { object: "schedule" });
          props.onClickClose();
        })
        .catch((error) => {
          const { message, data: { overlappingDays = [] } = {} } =
            error.response?.data || {};
          if (
            message ===
            "creation_schedule_failed_because_of_superposition_schedule_employee"
          )
            setNotificationError(
              dispatch,
              <CustomNotificationError overlappingDays={overlappingDays} />
            );
          else showError(message || "error");
          console.error(error);
        })
        .finally(() => {
          setSaving(false);
          setValidated(false);
        });
    }
  }

  function handleFormError(errors) {
    console.log("errors", errors);
    const errorArray = Object.values(errors);
    if (
      errorArray.some((err) => err.type === "required") ||
      errors.dateRange?.type === "isRequired"
    ) {
      showError("field_obligatory");
      return;
    }

    if (
      [errors, ...Object.values(errors.weeklyHours || {})].some(
        (error) => error.endTime?.type === "isAfterStartDate" ||
          error.startTime?.type === "isAfterStartDate"
      )
    ) {
      showError("verification_two_object", {
        object1: "endDate",
        operator: "superiour",
        object2: "startDate"
      });
      return;
    }

    if (errors.dateRange?.type === "isLessThanOneDay") {
      showError("L'horaire de travail ne doit pas dépasser les 24 heures");
      return;
    }

    if (
      [errors.lunchEndDate?.type, errors.lunchStartDate?.type].includes(
        "isBetweenStartAndEnd"
      )
    ) {
      showError(
        "Les heures de pause doivent êtres compris entre les heures de début et de fin"
      );
      return;
    }

    if (errors.lunchEndDate?.type === "isAfterLunchStart") {
      showError(
        "Pour la pause, heure de fin doit être supérieur à heure de début"
      );
      return;
    }

    if (errors.recurrenceEndDate?.type === "isAfterEndDate") {
      showError("please_verify", { object: "endDateRecurence" });
      return;
    }

    if (
      [errors.startTime?.type, errors.endTime?.type].includes(
        "isDurationGreaterThanOneMinute"
      )
    ) {
      showError("allowed_min_agenda");
      return;
    }
  }

  const onExpandedDayChange = useCallback(
    (dayKey, forceOpen = false) =>
      setExpandedDayKeys((_expandedDayKeys) => {
        if (forceOpen) {
          return _expandedDayKeys.includes(dayKey)
            ? _expandedDayKeys
            : [..._expandedDayKeys, dayKey];
        }
        return _expandedDayKeys.includes(dayKey)
          ? _expandedDayKeys.filter((_dayKey) => _dayKey !== dayKey)
          : [..._expandedDayKeys, dayKey];
      }),
    []
  );

  return (
    <ModalVisibility
      isOpen={props.isOpen}
      onClickClose={props.onClickClose}
      isLoading={isSaving}
      buttonOptions={{
        click: handleSubmit(onSaveClicked, handleFormError),
        color: "orange",
        title: hourTranslator("Enregistrer")
      }}
      buttonCancelOptions={{
        click: props.onClickClose,
        color: "outlined",
        title: hourTranslator("Annuler")
      }}
      control={control}
      WithMarge="arh-w-[56.75rem]"
      colorCheckbox="orange"
      title={hourTranslator("Attribuer à")}
      formValidation={formValidation}
      WithSpaceLoading={false}
      withVisibility
      optionsVisibility={{
        authorizedEmployees: props.agendaModel?.CombinedEmployees || [],
        type: "new",
        tooltipType: "white",
        variant: "darkOrange",
        withToolTip: true
      }}
      // @ts-ignore
      useFormFunction={{ watch }}
      background="!arh-bg-[#F4F7F8]"
      customClassParent={{
        right: { width: "arh-w-[52%]" },
        left: {
          width: "arh-w-[48%]",
          inside: { top: "arh-mt-[0.4rem]" },
          title: { margin: "arh-mb-4 arh-mt-[4.75rem]" }
        }
      }}
    >
      <FormProvider {...methods}>
        <div className="arh-m-0 arh-w-full arh-p-0">
          <div className="arh-mt-[-0.25rem] arh-w-full arh-font-poppins arh-text-xl arh-font-semibold arh-text-black mobile-768:arh-text-base ">
            {hourTranslator(
              props.isHourModel
                ? props.agendaModel?._id
                  ? "Modifier un modèle d'horaire"
                  : "Créer un modèle d'horaire"
                : "Création horaire"
            )}
          </div>
          <div className="arh-relative arh-mb-3 arh-flex arh-w-full arh-flex-col arh-items-start">
            <div className="arh-flex arh-min-w-[50%] arh-justify-between">
              <div className="flex-justify-center arh-my-[0.875rem] arh-mr-8 arh-flex">
                <label className="label_value_hours arh-flex arh-items-center">
                  {SvgCalendarBlack()}
                  <span className="arh-ml-1.5 arh-font-poppins arh-text-[13px] arh-font-medium">
                    {headerDateText || "--"}
                  </span>
                </label>
              </div>
              <div className="flex-justify-center arh-mx-8 arh-my-[0.875rem] arh-flex">
                <label className="arh-flex arh-items-center">
                  {SvgHourBlack()}
                  <span className="arh-ml-1.5 arh-font-poppins arh-text-[13px] arh-font-medium">
                    {headerTimeText || "--"}
                  </span>
                </label>
              </div>
            </div>
          </div>
          <Controller
            control={control}
            name="isTeleworkActive"
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <TeleworkButton
                onClickOffice={() => {
                  onChange(true);
                }}
                onClickRemote={() => {
                  onChange(false);
                }}
                value={value}
              />
            )}
          />

          <div className="arh-relative arh-flex arh-w-full arh-justify-center">
            <div className="arh-w-full">
              {
                <div className="arh-mb-2">
                  {props.isHourModel ? (
                    <Controller
                      control={control}
                      name="name"
                      defaultValue=""
                      rules={{ required: true }}
                      render={({
                        field: { onChange, onBlur, value },
                        fieldState: { invalid }
                      }) => (
                        <FormSimple
                          label={hourTranslator("Nom du modèle")}
                          placeholder={hourTranslator("Nom du modèle")}
                          classLabel="arh-flex"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                          invalid={invalid}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      control={control}
                      name="agendaModel"
                      defaultValue={null}
                      render={({ field: { value, name } }) => (
                        <ListboxSimple
                          label={hourTranslator("Sélectionner un modèle")}
                          placeholder={hourTranslator("Modèle")}
                          name={name}
                          optionList={sortedAgendaModels}
                          selectedValue={value}
                          setSelectedValue={resetByAgendModel}
                          showReset
                          noRadio
                        />
                      )}
                    />
                  )}
                </div>
              }
              <div className="h-mt">
                <Controller
                  control={control}
                  name="workingPlace"
                  defaultValue=""
                  render={({
                    field: { onChange, onBlur, value },
                    fieldState: { invalid }
                  }) => (
                    <FormSimple
                      label={hourTranslator("Spécifier un lieu")}
                      placeholder={hourTranslator("Lieu")}
                      classLabel="arh-flex"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      invalid={invalid}
                    />
                  )}
                />
              </div>
              <div className="arh-relative arh-mb-4 arh-mt-2">
                <div className="arh-relative">
                  <Controller
                    control={control}
                    name="color"
                    defaultValue=""
                    render={({ field: { name, value } }) => (
                      <input
                        name={name}
                        type="hidden"
                        value={value}
                      />
                    )}
                  />
                  <div ref={colorPickerTriggerRef}>
                    <FormGroupColorPicker
                      label={hourTranslator("Sélectionnez une couleur")}
                      placeholder={`${hourTranslator("Code couleur")} (#DFBEFF)...`}
                      className="input_group input_for_colorPicker"
                      name="titleFreeText"
                      onChange={onChangeColorPicker}
                      value={color?.valueHexadecimale?.toString() || ""}
                      stylePreviewColor={
                        color?.valueHexadecimale?.toString() || "#F9FAFC"
                      }
                      onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                    />
                  </div>
                  {isColorPickerOpen && (
                    <div
                      ref={colorPickerRef}
                      className="modalColorPicker"
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "0",
                        zIndex: 9999
                      }}
                    >
                      <div className="content_colorPicker arh-relative !arh-px-3">
                        <div className="arrow-up arh-absolute"></div>
                        <span className="label_colorPicker medium arh-flex">
                          {hourTranslator("choisissez un échantillon")} :{" "}
                        </span>
                        <div className="items_colorPicker position_relative">
                          {/* @ts-ignore */}
                          <SketchPicker
                            color={color?.valueHexadecimale?.toString()}
                            onChangeComplete={(color) => {
                              handleChangeComplete(color);
                              setIsColorPickerOpen(false);
                            }}
                            presetColors={presetColors}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Controller
                control={control}
                name="dateRange"
                defaultValue={{}}
                rules={formValidation.dateRange}
                render={({
                  field: { value, name, onChange },
                  fieldState: { invalid }
                }) => (
                  <div className="arh-mt-2 arh-flex arh-flex-col arh-space-y-1">
                    <label
                      className={`
                            arh-mb-0 arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium 
                            ${invalid ? " arh-text-[#F60A20] " : " arh-text-black "} 
                          `}
                    >
                      {hourTranslator("Date/Début de la période")}
                    </label>
                    <div className="dropdown_content_hour items_filter datepicker_modals_parcours datePicker_posteAvailable blackSvgColor arh-w-full">
                      <div className=" dateRange_green DateRangePicker_customisation DateRangePicker_customisation--orange arh-relative">
                        <DateRangePickerDropdown
                          key=""
                          classParent="pickerWorkingHour"
                          className="input_simple focus_orange"
                          textInput={hourTranslator("Aujourd'hui")}
                          name={name}
                          SaveCloseRange={(range) => {
                            const newRange = range?.[0];
                            onChange(newRange || {});
                            if (newRange?.startDate && newRange?.endDate) {
                              WEEK_DAYS.map(({ _id: dayIndex, key }) =>
                                setValue(`weeklyHours.${key}.dateRange`, {
                                  startDate: moment(newRange.startDate)
                                    .isoWeekday(dayIndex)
                                    .toDate(),
                                  endDate: moment(newRange.endDate)
                                    .isoWeekday(dayIndex)
                                    .toDate()
                                })
                              );
                            }
                          }}
                          dateSelected={value}
                          // selected={value}
                          classSwitch="c-switch-pill-checked--orange--color--to--left"
                          classBackground="orange"
                          invalid={invalid}
                          cancelMode={true}
                          // classDatePicker="!arh-top-[4.4rem] !arh-w-full"
                          // customModal={true}
                        />
                      </div>
                    </div>
                  </div>
                )}
              />
              {type !== "weekly" ? (
                <>
                  <div className="list_employer_create_hours blackSvgColor arh-mt-2">
                    <InputTimepickerModal
                      validateOnSubmit
                      title={hourTranslator("Horaires")}
                      control={control}
                      formValidation={formValidation}
                    />
                  </div>
                  <div className="arh-mt-4 arh-flex arh-w-full">
                    <div className="arh-flex arh-items-center arh-space-x-4 ">
                      <label className="!arh-mb-0 arh-font-poppins arh-text-xs arh-font-medium arh-text-black">
                        {hourTranslator("Inclure une pause-repas")}
                      </label>
                      <Switch
                        color="arh-bg-orangeColor"
                        checked={isLunchHourShowed}
                        handleChange={toggleLunchHour}
                      />
                    </div>
                  </div>
                  {isLunchHourShowed && (
                    <LunchHoursPicker
                      control={control}
                      formValidation={formValidation}
                      t={hourTranslator}
                    />
                  )}
                </>
              ) : (
                <WeeklyHoursSection
                  t={hourTranslator}
                  control={control}
                  setValue={setValue}
                  expandedDayKeys={expandedDayKeys}
                  onExpandedDayChange={onExpandedDayChange}
                  formValidation={formValidation}
                />
              )}
            </div>
          </div>
          <div className="arh-mt-2">
            <label className="arh-mb-1 arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium">
              {hourTranslator("Notes")}
            </label>
            <Controller
              control={control}
              name="note"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <TextArea
                  name={name}
                  value={value}
                  placeholder={hourTranslator("Notes")}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div className={`${isColorPickerOpen ? "!arh-mt-16" : ""}`}>
            <RecurrenceInput
              control={control}
              getValues={getValues}
              setValue={setValue}
              setRecurrenceShowed={setRecurrenceShowed}
              recurrenceShowed={isRecurrenceShowed}
              formValidation={formValidation}
              watch={watch}
              showDaysRepetition={type !== "weekly"}
            />
          </div>
          <div className="!arh-my-4 arh-flex arh-flex-col arh-items-start arh-font-poppins arh-text-xs arh-font-normal arh-text-darkColor">
            <Controller
              control={control}
              name="escludePublicHolidays"
              render={({ field: { onChange, value, name } }) => (
                <div className="arh-mb-2 arh-flex arh-items-center">
                  <Switch
                    checked={value}
                    color="arh-bg-orangeColor"
                    handleChange={onChange}
                    name={name}
                  />
                  <span className="arh-ml-3">
                    {hourTranslator(
                      "Ne pas ajouter d'horaires les journées fériées"
                    )}
                  </span>
                </div>
              )}
            />
            <Controller
              control={control}
              name="escludeSpecialHolidays"
              render={({ field: { onChange, value, name } }) => (
                <div className="arh-mb-2 arh-flex arh-items-center">
                  <Switch
                    checked={value}
                    color="arh-bg-orangeColor"
                    handleChange={onChange}
                    name={name}
                  />
                  <span className="arh-ml-3">
                    {hourTranslator(
                      "Ne pas ajouter d'horaires les journées spéciales"
                    )}
                  </span>
                </div>
              )}
            />
            <Controller
              control={control}
              name="escludeAcceptedLeaves"
              render={({ field: { onChange, value, name } }) => (
                <div className="arh-mb-2 arh-flex arh-items-center">
                  <Switch
                    checked={value}
                    color="arh-bg-orangeColor"
                    handleChange={onChange}
                    name={name}
                  />
                  <span className="arh-ml-3">
                    {hourTranslator(
                      "Ne pas ajouter d'horaires les journées contenants des vacances et absences acceptées"
                    )}
                  </span>
                </div>
              )}
            />
          </div>
        </div>
      </FormProvider>
    </ModalVisibility>
  );
}
export default ModalHourCreate;
